export type ItemSection = 'answers' | 'submsges'

export type ItemLocation = 'active' | 'drafts' | 'trash'
export type ItemActionContext = ItemLocation | 'favorites'

export type ItemActionId =
  | 'move-to-drafts'
  | 'move-to-trash'
  | 'restore-to-active'
  | 'add-favorite'
  | 'remove-favorite'
  | 'purge'

export type ItemFavoriteActionId = 'add-favorite' | 'remove-favorite'

export type LegacyMoveDestination = 'subs' | 'drafts' | 'trash'
export type LegacyMoveSource = 'msg' | 'drafts' | 'trash'
export type LegacyItemPagingMode = LegacyMoveSource | 'favs'

export type ItemActionContract =
  | {
      id: 'move-to-drafts' | 'move-to-trash' | 'restore-to-active'
      kind: 'relocate'
      destination: ItemLocation
      legacyDestination: LegacyMoveDestination
    }
  | {
      id: 'add-favorite' | 'remove-favorite'
      kind: 'membership'
      collection: 'favorites'
      isMember: boolean
    }
  | {
      id: 'purge'
      kind: 'purge'
      legacyDestination: 'trash'
    }

export const itemActionContracts: Readonly<Record<ItemActionId, ItemActionContract>> = {
  'move-to-drafts': {
    id: 'move-to-drafts',
    kind: 'relocate',
    destination: 'drafts',
    legacyDestination: 'drafts',
  },
  'move-to-trash': {
    id: 'move-to-trash',
    kind: 'relocate',
    destination: 'trash',
    legacyDestination: 'trash',
  },
  'restore-to-active': {
    id: 'restore-to-active',
    kind: 'relocate',
    destination: 'active',
    legacyDestination: 'subs',
  },
  'add-favorite': {
    id: 'add-favorite',
    kind: 'membership',
    collection: 'favorites',
    isMember: true,
  },
  'remove-favorite': {
    id: 'remove-favorite',
    kind: 'membership',
    collection: 'favorites',
    isMember: false,
  },
  purge: {
    id: 'purge',
    kind: 'purge',
    legacyDestination: 'trash',
  },
}

const itemActionsByContext: Readonly<Record<ItemActionContext, readonly ItemActionId[]>> = {
  active: ['move-to-trash', 'move-to-drafts', 'add-favorite'],
  drafts: ['move-to-trash', 'restore-to-active'],
  trash: ['restore-to-active', 'purge'],
  favorites: ['remove-favorite'],
}

export function itemActionsForContext(context: ItemActionContext): readonly ItemActionId[] {
  return itemActionsByContext[context]
}

/** The bulk-action context for a desk view mode (was inlined in every
 * CheckedsInfo copy): drafts/trash map to themselves, favs to favorites,
 * everything else acts on the active tree. */
export function actionContextForDeskMode(mode: string): ItemActionContext {
  if (mode === 'drafts' || mode === 'trash') return mode
  return mode === 'favs' ? 'favorites' : 'active'
}

export function isItemActionAllowed(context: ItemActionContext, action: ItemActionId): boolean {
  return itemActionsByContext[context].includes(action)
}

export function resolveItemAction(context: ItemActionContext, action: ItemActionId): ItemActionContract {
  if (!isItemActionAllowed(context, action)) {
    throw new Error(`[item movements] Action "${action}" is not allowed from "${context}"`)
  }

  return itemActionContracts[action]
}

export function resolveItemFavoriteAction(action: ItemFavoriteActionId): Extract<ItemActionContract, { kind: 'membership' }> {
  return itemActionContracts[action] as Extract<ItemActionContract, { kind: 'membership' }>
}

export function legacyMoveSourceForContext(context: ItemActionContext): LegacyMoveSource | null {
  if (context === 'favorites') return null
  return context === 'active' ? 'msg' : context
}

export function legacyPagingModeForContext(context: ItemActionContext): LegacyItemPagingMode {
  if (context === 'favorites') return 'favs'
  return legacyMoveSourceForContext(context) || 'msg'
}
